/* AUTO-SYNCED from PsyKit.io/hooks/use-bilateral-mirror.ts — DO NOT EDIT. Regenerate with `npm run sync`. */
"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { getSocket } from "@/lib/socket-client";
import { safeStorageGet, safeStorageSet, safeStorageRemove } from "@/lib/safe-storage";
import type { BroadcastPayload } from "@/hooks/use-bilateral-broadcast";
import type { CheckinActive, CheckinLiveData } from "@/lib/checkin-types";

export interface MirrorState {
  xNorm: number;
  /** Tempo in true Hz (0.5–2.5). Passed to MirrorCanvas for sinusoidal extrapolation. */
  hz: number;
  /** Elapsed seconds from therapist's session start at the time of this frame. */
  elapsedSeconds: number;
  ballSize: number;
  ballColor: string;
  canvasBg: string;
  isRunning: boolean;
  connected: boolean;
  audioOn: boolean;
  audioVolume: number;
  /** "theta"|"alpha"|"delta"|"off", SOUND_LIBRARY id, or "custom:<uuid>" (client skips). */
  audioPreset: string;
  /** Set to true when the therapist explicitly ends the session. Triggers post-checkin on client. */
  sessionEnded: boolean;
  /** Set to true when the therapist starts the session (pre-checkin complete). */
  sessionStarted: boolean;
  /** Set to true when the therapist has saved the session record. Closes client post-checkin. */
  sessionSaved: boolean;
  /** Whether the therapist has enabled controller-haptics. The client view's
   *  useGamepadHaptics hook arms when this is true; stays inert otherwise. */
  hapticsOn: boolean;
  /** Pulse intensity 0..1 — passed straight through to the client's hook. */
  hapticsIntensity: number;
  /** Which check-in form the therapist has pushed to us (null = none).
   *  Therapist-driven only — the client never shows a check-in on its own. */
  activeCheckin: CheckinActive;
  /** Latest live check-in answers (edited by either side, mirrored here). */
  checkinData: CheckinLiveData | null;
}

const TIMEOUT_MS = 3000;

/**
 * Subscribes to a Socket.IO room for the given session code and returns live
 * ball state. Works across any device or browser.
 *
 * The client uses (hz + elapsedSeconds) to run the same sinusoidal formula as
 * the therapist, extrapolating between frames at 60fps for smooth motion.
 *
 * Also exposes `sendCheckinComplete(phase)` so the client can notify the
 * therapist when a check-in phase is finished.
 */
export function useBilateralMirror(
  sessionCode: string
): MirrorState & {
  sendCheckinComplete: (phase: "pre" | "post") => void;
  broadcastCheckinData: (data: CheckinLiveData) => void;
} {
  // Restore one-time event flags from sessionStorage so a page refresh
  // doesn't reset the session lifecycle (or which check-in is open).
  const [state, setState] = useState<MirrorState>(() => {
    let sessionStarted = false;
    let sessionEnded = false;
    let sessionSaved = false;
    let activeCheckin: CheckinActive = null;
    if (sessionCode) {
      // safeStorageGet: Safari throws "The operation is insecure." on storage
      // access when blocked; an unguarded read here (a useState initializer)
      // would crash the whole /view page via the error boundary.
      sessionStarted = safeStorageGet("session", `psykit_bilateral_${sessionCode}_started`) === "1";
      sessionEnded   = safeStorageGet("session", `psykit_bilateral_${sessionCode}_ended`)   === "1";
      sessionSaved   = safeStorageGet("session", `psykit_bilateral_${sessionCode}_saved`)   === "1";
      const ck = safeStorageGet("session", `psykit_bilateral_${sessionCode}_checkin`);
      if (ck === "pre" || ck === "post") activeCheckin = ck;
    }
    return {
      xNorm: 0.5,
      hz: 1.0,
      elapsedSeconds: 0,
      ballSize: 30,
      ballColor: "#4a6fa5",
      canvasBg: "#0D1B2E",
      isRunning: false,
      connected: false,
      audioOn: false,
      audioVolume: 0.7,
      audioPreset: "off",
      sessionEnded,
      sessionStarted,
      sessionSaved,
      hapticsOn: false,
      hapticsIntensity: 0.6,
      activeCheckin,
      checkinData: null,
    };
  });

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Cache the client's own last check-in edit so we can re-emit it if the
  // therapist's socket re-joins (the server pings us with sync_request).
  const lastClientCheckinDataRef = useRef<CheckinLiveData | null>(null);

  useEffect(() => {
    if (!sessionCode) return;

    const socket = getSocket();
    socket.emit("join", { sessionCode });

    const resetTimeout = () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setState((prev) => ({ ...prev, connected: false }));
      }, TIMEOUT_MS);
    };

    const handleFrame = (payload: BroadcastPayload) => {
      setState((prev) => ({
        xNorm: payload.xNorm ?? 0.5,
        hz: payload.hz ?? 1.0,
        elapsedSeconds: payload.elapsedSeconds ?? 0,
        ballSize: payload.ballSize ?? 30,
        ballColor: payload.ballColor ?? "#4a6fa5",
        canvasBg: payload.canvasBg ?? "#0D1B2E",
        isRunning: payload.isRunning ?? false,
        connected: true,
        audioOn: payload.audioOn ?? false,
        audioVolume: payload.audioVolume ?? 0.7,
        audioPreset: payload.audioPreset ?? "off",
        sessionEnded: prev.sessionEnded,
        sessionStarted: prev.sessionStarted,
        sessionSaved: prev.sessionSaved,
        hapticsOn: payload.hapticsOn ?? false,
        hapticsIntensity: payload.hapticsIntensity ?? 0.6,
        activeCheckin: prev.activeCheckin,
        checkinData: prev.checkinData,
      }));
      resetTimeout();
    };

    const handleSessionEnd = () => {
      safeStorageSet("session", `psykit_bilateral_${sessionCode}_ended`, "1");
      setState((prev) => ({ ...prev, sessionEnded: true, isRunning: false }));
    };
    const handleSessionStart = () => {
      safeStorageSet("session", `psykit_bilateral_${sessionCode}_started`, "1");
      setState((prev) => ({ ...prev, sessionStarted: true }));
    };
    const handleSessionSaved = () => {
      safeStorageSet("session", `psykit_bilateral_${sessionCode}_saved`, "1");
      setState((prev) => ({ ...prev, sessionSaved: true }));
    };

    // Therapist pushed (or dismissed) a check-in form. Persist so a refresh
    // keeps it open. This is the ONLY thing that opens a check-in on the client.
    const handleCheckinState = ({ active }: { active: CheckinActive }) => {
      const next: CheckinActive = active === "pre" || active === "post" ? active : null;
      if (next) safeStorageSet("session", `psykit_bilateral_${sessionCode}_checkin`, next);
      else safeStorageRemove("session", `psykit_bilateral_${sessionCode}_checkin`);
      setState((prev) => ({ ...prev, activeCheckin: next }));
    };
    // Inbound live answers from the therapist (or our own replay). Stored as-is;
    // ClientView reconciles this into its editable copy WITHOUT re-broadcasting.
    const handleCheckinData = (payload: CheckinLiveData) => {
      setState((prev) => ({ ...prev, checkinData: payload }));
    };

    // The server pings us with sync_request when another socket joins. If we
    // have unsynced local check-in edits, re-emit them so a (re)joining
    // therapist sees the client's current answers.
    const handleSyncRequest = () => {
      if (lastClientCheckinDataRef.current) {
        getSocket().emit("checkin_data", lastClientCheckinDataRef.current);
      }
    };

    socket.on("frame", handleFrame);
    socket.on("session_end", handleSessionEnd);
    socket.on("session_start", handleSessionStart);
    socket.on("session_saved", handleSessionSaved);
    socket.on("checkin_state", handleCheckinState);
    socket.on("checkin_data", handleCheckinData);
    socket.on("sync_request", handleSyncRequest);

    resetTimeout();

    return () => {
      socket.off("frame", handleFrame);
      socket.off("session_end", handleSessionEnd);
      socket.off("session_start", handleSessionStart);
      socket.off("session_saved", handleSessionSaved);
      socket.off("checkin_state", handleCheckinState);
      socket.off("checkin_data", handleCheckinData);
      socket.off("sync_request", handleSyncRequest);
      socket.emit("leave", { sessionCode });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [sessionCode]);

  const sendCheckinComplete = useCallback((phase: "pre" | "post") => {
    getSocket().emit("checkin_complete", { phase });
  }, []);

  /** Push the client's check-in edits to the therapist (live sync). */
  const broadcastCheckinData = useCallback((data: CheckinLiveData) => {
    lastClientCheckinDataRef.current = data;
    getSocket().emit("checkin_data", data);
  }, []);

  return { ...state, sendCheckinComplete, broadcastCheckinData };
}
